import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MapValues } from '../components/map/map.values';
import FieldsSettings from '../forms/fields/fields.settings';
import FormsHelpers from '../forms/forms.helpers';
import { SelectorOption } from '../interfaces/form-and-field.interface';

@Injectable()
export class SuggestionsHintsService {
  private organizationTypeSuggestions: SelectorOption[] = [];
  private individualRolesSuggestions: SelectorOption[] = [];

  private translateService = inject(TranslateService);

  public get organizationTypeOptions(): SelectorOption[] {
    return this.organizationTypeSuggestions;
  }

  public get individualRolesOptions(): SelectorOption[] {
    return this.individualRolesSuggestions;
  }

  /**
   * @description Translates diseases hints on app initialization. Should be called in
   *              case changing the used language for translation.
   * @returns { Promise<SelectorOption[][]> }
   */
  public translateSuggestions(): Promise<SelectorOption[][]> {
    return Promise.all([this.translateOrganizationTypeSuggestions(), this.translateIndividualRoleSuggestions()]);
  }

  /**
   * @description Does organization types hints translating.
   *              Comment: this.translateService.get('BTN_OK_HINT') was  executed as fake
   *              because of particular qualities of translateService in using instant() method.
   * @returns { Promise<SelectorOption[]> }
   */
  private translateOrganizationTypeSuggestions(): Promise<SelectorOption[]> {
    return new Promise((resolve: (value: SelectorOption[]) => void) => {
      this.translateService.get('BTN_OK_HINT').subscribe(() => {
        this.organizationTypeSuggestions = this.formSelectOption(
          FormsHelpers.notCustomOrganizationTypes,
          FieldsSettings.ORGANIZATION_TYPE_PREFIX,
        );
        resolve(this.organizationTypeOptions);
      });
    });
  }

  /**
   * @description Does individual roles hints translating.
   *              Comment: this.translateService.get('BTN_OK_HINT') was  executed as fake
   *              because of particular qualities of translateService in using instant() method.
   * @returns { Promise<SelectorOption[]> }
   */
  private translateIndividualRoleSuggestions(): Promise<SelectorOption[]> {
    return new Promise((resolve: (value: SelectorOption[]) => void) => {
      this.translateService.get('BTN_OK_HINT').subscribe(() => {
        this.individualRolesSuggestions = this.formSelectOption(
          MapValues.mapIndividualRolesKeys,
          FieldsSettings.INDIVIDUAL_ROLE_PREFIX,
        );
        resolve(this.individualRolesOptions);
      });
    });
  }

  /**
   * @description Does options list mapping to SelectorOption items list and then sorts
   *              it in alphabetical order.
   * @param { string[] } options
   * @param { string } prefix
   */
  private formSelectOption(options: string[], prefix: string): SelectorOption[] {
    return options
      .map((option: string) => ({
        entity_id: option,
        title: this.translateService.instant(`${prefix}${option}`) as string,
      }))
      .sort((a: SelectorOption, b: SelectorOption) => a.title.localeCompare(b.title));
  }
}
